import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { PageHero, SectionHead, Reveal } from "@/components/bits/Reveal";
import EmployerForm from "@/components/forms/EmployerForm";
import { IMAGES, EMPLOYER_STEPS, INDUSTRIES } from "@/data/content";
import { useSEO } from "@/lib/seo";

export default function Businesses() {
  useSEO(
    "For Employers & Businesses | Request Manpower — Maqline Solutions Pvt Ltd",
    "Request manpower from Maqline Solutions Pvt Ltd for production, manufacturing and operational roles. Manpower outsourcing, contract staffing and recruitment support for industrial organizations."
  );
  const { hash } = useLocation();
  useEffect(() => {
    if (hash === "#request") {
      const el = document.getElementById("request");
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 300);
    }
  }, [hash]);

  return (
    <main data-testid="businesses-page">
      <PageHero
        overline="For Businesses"
        titleLines={["Your Workforce Requirements.", "Our Recruitment Support."]}
        description="Dependable manpower for production, manufacturing and operations — sourced, screened, deployed and coordinated by a team that understands industrial work."
        image={IMAGES.audienceBusiness}
      />

      <section className="bg-white py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <SectionHead overline="How It Works" title="From requirement to deployed workforce" description="A transparent six-step engagement. You always know what happens next." />
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-ink/10 border border-ink/10">
            {EMPLOYER_STEPS.map((s, i) => (
              <Reveal key={s.num} delay={(i % 3) * 0.08} className="bg-white">
                <div className="p-8 h-full group hover:bg-steel transition-colors duration-400">
                  <span className="font-mono text-3xl text-gold/60 group-hover:text-gold font-bold transition-colors duration-300">{s.num}</span>
                  <h3 className="font-display text-xl text-ink mt-4 mb-2">{s.title}</h3>
                  <p className="text-sm text-muted leading-relaxed">{s.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-steel py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-2 gap-14">
          <div>
            <SectionHead overline="What You Can Request" title="Manpower categories we source" />
            <Reveal delay={0.15}>
              <div className="mt-8 flex flex-wrap gap-2.5">
                {["Production", "Machine Operators", "Assembly", "Quality Checking", "Fitter", "Technician", "Helpers", "Industrial Support", "Warehouse", "Maintenance", "Supervisors", "Trainees"].map((c) => (
                  <span key={c} className="text-sm font-semibold text-navy bg-white border border-ink/10 px-4 py-2 rounded-sm">{c}</span>
                ))}
              </div>
            </Reveal>
            <Reveal delay={0.22}>
              <div className="mt-10">
                <p className="text-[11px] uppercase tracking-[0.2em] font-bold text-gold mb-4">Industries served</p>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {INDUSTRIES.map((i) => (
                    <li key={i.num} className="text-sm text-ink/75 flex items-center gap-2.5">
                      <span className="w-1.5 h-1.5 bg-gold rotate-45 shrink-0" /> {i.name}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.1}>
            <div className="bg-darknavy noise-bg rounded-sm p-8 sm:p-10 text-white">
              <p className="font-mono text-4xl sm:text-5xl font-bold text-lightgold">600+</p>
              <p className="text-sm text-white/60 mt-2 mb-8">Candidates deployed across our client network</p>
              <div className="space-y-4 text-sm text-white/70 leading-relaxed">
                <p>Our deployments span automotive, electronics, precision components, defence manufacturing and solar organizations across our client network.</p>
                <p className="text-white/50">Every engagement includes structured coordination after deployment, because a workforce partner's job does not end at joining day.</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section id="request" className="bg-white py-16 sm:py-24 scroll-mt-20" data-testid="employer-form-section">
        <div className="max-w-4xl mx-auto px-5 sm:px-8">
          <SectionHead overline="Submit Manpower Requirement" title="Tell us what your operation needs" description="Share your requirement below — our team will review it and contact you shortly to discuss sourcing, timelines and deployment." />
          <Reveal delay={0.2}>
            <div className="mt-12 border border-ink/10 rounded-sm p-6 sm:p-10 bg-steel/50">
              <EmployerForm />
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
